import React, {useEffect, useMemo, useState} from 'react';
import {BaselineCmdsInfo} from '../utils/baselineCmds';
import {
	Dialog,
	DialogFooter,
	DialogType,
	IDialogContentProps,
	PrimaryButton,
	TextField,
} from '@fluentui/react';
import {useVehicleProjectsTranslation} from 'features/VehicleProjects/hooks/vehicleProjectsTranslationHooks';
import {
	NavigateToBaselineListPage,
	useNavigateToBaselineListPage,
} from '../BaselineListPage/BaselinesPage.utils';
import {
	GetBaselinesByModelAndSeriesDocument,
	useCreateBaseLineByModelAndSeriesMutation,
} from '../hooks/baseline.generated';
import {ControlledCheckbox} from 'components/hookForms';
import {useBaselineTransferForm} from '../hooks/useBaselineTransferForm';

interface Props {
	generation: string;
	modelSeries: string;
	infoForIsOpen: BaselineCmdsInfo['infoForIsSuccessDialogOpen'];
}

export function BaselineCreateDialog({
	generation,
	modelSeries,
	infoForIsOpen: [isOpen, {setFalse}],
}: Props): JSX.Element {
	const {t} = useVehicleProjectsTranslation({
		keyPrefix: 'BaselineCreateDialog',
	});

	const now = new Date();

	const defaultName = `TR:ACE Anforderungsliste für Modellreihe: ${modelSeries}, Generation: ${generation} vom ${now.toLocaleDateString(
		'de-DE',
	)} um ${now.toLocaleTimeString('default', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})} Uhr`;
	const transferForm = useBaselineTransferForm();
	const [name, setName] = useState(defaultName);
	const [creating, setCreating] = useState(false);

	const navigateToBaselinesPage: NavigateToBaselineListPage =
		useNavigateToBaselineListPage({
			generation,
			modelSeries,
		});

	const [createBaseLineByModelAndSeriesMutation] =
		useCreateBaseLineByModelAndSeriesMutation();

	const onTitleChange = (
		event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
		newValue?: string,
	) => {
		setName(newValue ?? '');
	};

	const handleSave = async () => {
		try {
			setCreating(true);

			const status = transferForm.getStatus();

			await createBaseLineByModelAndSeriesMutation({
				variables: {
					input: {
						name,
						generation,
						modelSeries,
						transferWorkflow: {
							transferToCodebeamer: status,
						},
					},
				},
				refetchQueries: [GetBaselinesByModelAndSeriesDocument],
			});
		} catch (error) {
			console.error('Failed to rename baseline:', error);
		}

		setFalse();

		navigateToBaselinesPage();
		setCreating(false);
	};

	const handleCancel = async () => {
		setFalse();
	};

	const dialogContentProps: IDialogContentProps = useMemo(() => {
		return {
			type: DialogType.normal,
			title: t('Title'),
			styles: {title: {wordBreak: 'break-word'}},
		};
	}, [t]);
	const checkboxStyles = {root: {paddingLeft: '5px', paddingTop: '5px'}};

	const isNameEmpty = !name;

	return (
		<Dialog
			hidden={!isOpen}
			onDismiss={setFalse}
			dialogContentProps={dialogContentProps}
		>
			<TextField
				value={name}
				onChange={onTitleChange}
				multiline
				required
				styles={{fieldGroup: {minHeight: '200px'}}}
				errorMessage={isNameEmpty ? t('EmptyNameError') : undefined}
			/>
			<ControlledCheckbox
				name='CodebeamerTransfer'
				label={t('CodebeamerTransfer')}
				control={transferForm.control}
				styles={checkboxStyles}
			/>
			<DialogFooter>
				<PrimaryButton onClick={handleCancel} disabled={false}>
					{t('CancelBtn')}
				</PrimaryButton>

				<PrimaryButton onClick={handleSave} disabled={isNameEmpty || creating}>
					{t('SaveBtn')}
				</PrimaryButton>
			</DialogFooter>
		</Dialog>
	);
}
